import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Typography } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { getIn, useFormik } from 'formik';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { FHConfirmDialog, FHSignatureDialog } from '../../../components/base';
import { getQuillPlainText } from '../../../components/baseV2';
import { BTSessionStatus, SessionNoteSignatures } from '../../../constants';
import { FeatureFlags } from '../../../constants/featureFlags';
import { capitalizeFirstLetter, history } from '../../../helpers';
import { useValidateDuration } from '../../../hooks/useValidateDuration';
import { sessionActions, treatmentPlanActions } from '../../../store';
import { getUtcOffset } from '../../../utils/common';
import { EvvQrDialog } from './components';
import {
  BTSignatureMessage,
  ParentSignatureMessage,
  VBTSignatureMessage,
} from './components/misc/SignatureMessages';
import VerifyParentSignature from './components/verifyParentSignature';
import GoalsProgress from './components/goalsProgress/GoalsProgress';
import SessionNarrative from './components/narrative/SessionNarrative';
import SymptomsAndInterventions from './components/symptomsInterventions';
import SessionNotesFooter from './SessionNoteFooter';
import {
  GUARDIAN_VERIFICATION_TOKEN_LIFESPAN_MS,
  SESSION_NOTE_VERSIONS,
  SessionNoteSteps,
  segmentSliderInitialState,
} from './constants';
import { EVV_TOKEN_KINDS, EVV_TOKEN_STATUSES } from '../constants';
import { useEvvVerification, useSaveGoalsProgress } from './hooks';
import { useGuardianProviderAssignments } from './hooks/useGuardianProviderAssignments';
import { useGuardianVerificationLifecycle } from './hooks/useGuardianVerificationLifecycle';
import {
  getInvalidGoalTechniqueList,
  getInvalidGoalTimingList,
  getInvalidGoalList,
  getInvalidTrialList,
  getSessionNarrativeLength,
  hasNoFieldChecked,
  validateCheckBoxFields,
} from './utils';
import {
  getInvalidTrialsMessage,
  getMissingFirstPageInfoMessage,
} from './sessionValidationMessages';
import { getGuardianAccessTokenSilently, getSignatureDialogHeaderText } from './utils/general';
import {
  createGuardianVerificationState,
  scheduleGuardianVerificationExpiry,
  verifyGuardianIdentity,
} from './components/verifyParentSignature/utils/general';
import { useSegmentSliderStoreData } from './hooks/useSegmentSliderStoreData';

import type {
  IProviderAssignment,
  ISessionGoalsAndTrials,
  ITreatmentPlanGoal,
  SelectValueType,
} from '../../../utils/types';
import type {
  IGuardianSignaturePayload,
  IGuardianVerificationState,
  ISegmentSliderProps,
  SegmentSliderFormValues,
  SessionPageErrors,
} from './types';

function SegmentSlider({
  isBay7Active,
  isEditable,
  isVBT,
  isPRBT,
  updateDisabled,
  isBay7InPersonGuardianFlow,
}: ISegmentSliderProps) {
  // constants
  const { getAccessTokenSilently } = useAuth0();
  const { t } = useTranslation('sessionNotes');
  const dispatch = useDispatch();
  const sessionNotesV2Enabled = useBooleanFlagValue(
    FeatureFlags.SESSION_NOTES_V2_ENABLED,
    false,
  );

  // Redux state
  const { session, client, treatmentPlanGoals, user } = useSegmentSliderStoreData();
  const useNewUi =
    sessionNotesV2Enabled &&
    (!session || session.note_version === SESSION_NOTE_VERSIONS.V2);
  const {
    evvStatus,
    evvQrDialog,
    closeEvvQrDialog,
    startEvvVerification,
  } = useEvvVerification({
    sessionId: useNewUi && session?.evv_required ? session?.id : undefined,
  });

  // local state
  const [step, setStep] = useState(SessionNoteSteps.SymptomsAndInterventions);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [openSignature, setOpenSignature] = useState<boolean>(false);
  const [signatureStep, setSignatureStep] = useState<SessionNoteSignatures>(
    SessionNoteSignatures.BtSignature,
  );
  const [guardianProviderAssignments, setGuardianProviderAssignments] = useState<
    IProviderAssignment[]
  >([]);
  const [goalsPracticedList, setGoalsPracticedList] = useState<ISessionGoalsAndTrials | null>(null);
  const [sessionNarrativeMinimumLength, setSessionNarrativeMinimumLength] = useState<number>(180);
  const [symptomIntervationValidated, setSymptomIntervationValidated] = useState<boolean>(false);
  const [selectedParentEmail, setSelectedParentEmail] = useState<string>('');
  const [guardianVerification, setGuardianVerification] =
    useState<IGuardianVerificationState | null>(null);
  const [guardianVerificationLoading, setGuardianVerificationLoading] = useState<boolean>(false);
  const guardianVerificationTimeoutRef = useRef<number | null>(null);
  const [showErrors, setShowErrors] = useState<SessionPageErrors>({
    SymptomsInterventions: false,
    GoalsProgress: false,
    SessionNarrative: false,
  });

  const validateDuration = useValidateDuration({
    startDate: session?.date_start ?? '',
    duration: session?.expected_duration ?? 0,
  });

  const signatureDialogHeaderText = useMemo(
    () =>
      getSignatureDialogHeaderText(
        isPRBT,
        isBay7Active,
        session?.delivery_method,
        session?.status,
        signatureStep,
      ),
    [isPRBT, isBay7Active, session, signatureStep],
  );

  const isGuardianIdentityVerified = useMemo(
    () =>
      Boolean(
        guardianVerification &&
        guardianVerification.email === selectedParentEmail &&
        guardianVerification.expiresAt > Date.now(),
      ),
    [guardianVerification, selectedParentEmail],
  );

  const isParentSignatureCanvasDisabled =
    signatureStep === SessionNoteSignatures.ParentSignature &&
    isBay7InPersonGuardianFlow &&
    !isGuardianIdentityVerified;
  const isStartEvvCaptured = Boolean(
    session?.evv_start_captured ||
      evvStatus?.start.captured ||
      evvStatus?.start.token_status === EVV_TOKEN_STATUSES.CAPTURED,
  );
  const isEndEvvCaptured = Boolean(
    session?.evv_end_captured ||
      evvStatus?.end.captured ||
      evvStatus?.end.token_status === EVV_TOKEN_STATUSES.CAPTURED,
  );

  const clearGuardianVerificationCache = useCallback(() => {
    if (guardianVerificationTimeoutRef.current) {
      window.clearTimeout(guardianVerificationTimeoutRef.current);
      guardianVerificationTimeoutRef.current = null;
    }
    setGuardianVerification(null);
  }, []);

  const handleParentEmailChange = useCallback(
    (value: SelectValueType) => {
      setSelectedParentEmail(String(value));
      clearGuardianVerificationCache();
    },
    [clearGuardianVerificationCache],
  );

  const handleGuardianIdentityVerification = useCallback(async () => {
    if (!selectedParentEmail) {
      toast.error('Please select a parent email before verification');
      return;
    }

    try {
      setGuardianVerificationLoading(true);
      const token = await verifyGuardianIdentity({ guardianEmail: selectedParentEmail });
      const nextGuardianVerification = createGuardianVerificationState({
        token,
        email: selectedParentEmail,
        tokenLifespanMs: GUARDIAN_VERIFICATION_TOKEN_LIFESPAN_MS,
      });
      setGuardianVerification(nextGuardianVerification);

      guardianVerificationTimeoutRef.current = scheduleGuardianVerificationExpiry({
        currentTimeoutId: guardianVerificationTimeoutRef.current,
        tokenLifespanMs: GUARDIAN_VERIFICATION_TOKEN_LIFESPAN_MS,
        onExpire: () => {
          setGuardianVerification(null);
          guardianVerificationTimeoutRef.current = null;
        },
      });
      toast.success('Guardian identity verified');
    } catch (error: any) {
      toast.error(error?.message || 'Guardian verification failed');
      clearGuardianVerificationCache();
    } finally {
      setGuardianVerificationLoading(false);
    }
  }, [clearGuardianVerificationCache, selectedParentEmail]);

  useGuardianProviderAssignments({
    sessionClientId: session?.client_id,
    userRoles: user?.roles,
    isBay7InPersonGuardianFlow,
    getAccessTokenSilently,
    setGuardianProviderAssignments,
  });

  useGuardianVerificationLifecycle({
    signatureStep,
    setSelectedParentEmail,
    clearGuardianVerificationCache,
    guardianVerificationTimeoutRef,
  });

  // Load initial values from sessions
  useEffect(() => {
    if (!session?.id) {
      resetForm();
      setStep(SessionNoteSteps.SymptomsAndInterventions);
      return;
    }
    setValues({ ...session });
    setSignatureStep(
      session.provider_signature && session.status === BTSessionStatus.AwaitingGuardianSignature
        ? SessionNoteSignatures.ParentSignature
        : SessionNoteSignatures.BtSignature,
    );
    setSelectedParentEmail('');
    clearGuardianVerificationCache();
  }, [clearGuardianVerificationCache, session?.id]);

  // Update min len required for narrative based session duration
  useEffect(() => {
    if (!session?.expected_duration) return;
    setSessionNarrativeMinimumLength(getSessionNarrativeLength(session.expected_duration));
  }, [session?.expected_duration]);

  useEffect(() => {
    if (!client) return;

    dispatch(
      treatmentPlanActions.getTreatmentPlanByQuery({
        query: { clientId: client.id },
        getAccessTokenSilently,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  const formik = useFormik<SegmentSliderFormValues>({
    initialValues: segmentSliderInitialState,
    onSubmit: async () => {
      return {};
    },
  });

  const { values, setFieldValue, setValues, resetForm } = formik;
  const saveGoalsProgress = useSaveGoalsProgress({
    sessionId: session?.id,
    values,
    goalsPracticedList,
    useNewUi,
  });

  const handleUpdateFieldValues = (key: string, value: any) => {
    if (key === 'narrative' || key === 'client_progress') {
      setFieldValue(key, value);
    } else {
      setFieldValue(key, { ...getIn(values, key), ...value });
    }
  };

  const getNarrativeText = (narrative?: string | null) =>
    useNewUi ? getQuillPlainText(narrative) : narrative?.trim() ?? '';

  const handleNextClick = async () => {
    if (!isEditable) {
      setStep(step + 1);
      return;
    }

    if (step === SessionNoteSteps.SymptomsAndInterventions) {
      await validateAndSaveSymptomIntervention();
    }

    if (step === SessionNoteSteps.GoalsProgress) {
      await validateAndSaveGoalsProgress();
    }

    if (step === SessionNoteSteps.SessionNarrative) {
      validateSessionNarrative();
    }
  };

  const validateAndSaveSymptomIntervention = async () => {
    if (updateDisabled) {
      setStep(step + 1);
      return;
    }
    setShowErrors((prev) => ({ ...prev, SymptomsInterventions: true }));
    setSymptomIntervationValidated(true);
    if (!symptomIntervationValidated && !validateCheckBoxFields(values)) {
      return;
    }
    setSubmitLoading(true);
    const data = {
      signs_and_symptoms_observed: values.signs_and_symptoms_observed,
      evidence_based_interventions_used: values.evidence_based_interventions_used,
    };
    const res = await dispatch(
      sessionActions.updateSymptomsInterventions({
        sessionId: session?.id,
        data,
        getAccessTokenSilently,
      }),
    );
    setSubmitLoading(false);
    if (!res || res.error) {
      return;
    }
    setStep(step + 1);
  };

  const validateAndSaveGoalsProgress = async () => {
    if (updateDisabled) {
      setStep(step + 1);
      return;
    }
    setShowErrors((prev) => ({ ...prev, GoalsProgress: true }));

    if (!values.client_progress) {
      toast.error('Please select client progress');
      return;
    }

    // Check if at least one goal exists .
    if (!goalsPracticedList || Object.keys(goalsPracticedList).length === 0) {
      toast.error('At least one goal must be recorded in a session.');
      return;
    }

    // validate if goals are valid (have at least one target)
    const invalidGoals = getInvalidGoalList(goalsPracticedList, treatmentPlanGoals);
    invalidGoals?.forEach((goal: ITreatmentPlanGoal) => {
      toast.error(
        'No targets have been recorded in the following goal: ' +
          capitalizeFirstLetter(goal?.goal_name?.label ?? ''),
      );
    });
    if (invalidGoals.length > 0) {
      return;
    }

    // validate if trials are valid
    const invalidTrials = getInvalidTrialList(goalsPracticedList);
    const invalidTrialsMessage = getInvalidTrialsMessage(invalidTrials);
    if (invalidTrialsMessage) {
      toast.error(invalidTrialsMessage);
    }
    if (invalidTrials.length > 0) {
      return;
    }

    // Show error when info is missing on the first page
    const clientSignError = hasNoFieldChecked(values, 'signs_and_symptoms_observed');
    const interventionError = hasNoFieldChecked(values, 'evidence_based_interventions_used');
    if (clientSignError || interventionError) {
      const errText = getMissingFirstPageInfoMessage(clientSignError, interventionError);
      toast.error(errText);
      return;
    }

    if (useNewUi) {
      const invalidTechniqueGoals = getInvalidGoalTechniqueList(
        goalsPracticedList,
        treatmentPlanGoals,
      );
      invalidTechniqueGoals?.forEach((goal: ITreatmentPlanGoal) => {
        toast.error(
          `${t('goalsPracticed.techniqueUsedRequired')}: ${capitalizeFirstLetter(
            goal?.goal_name?.label ?? '',
          )}`,
        );
      });
      if (invalidTechniqueGoals.length > 0) {
        return;
      }

      const invalidTimingGoals = getInvalidGoalTimingList(
        goalsPracticedList,
        treatmentPlanGoals,
        session?.expected_duration,
      );
      invalidTimingGoals?.forEach((goal: ITreatmentPlanGoal) => {
        toast.error(
          `${t('goalsPracticed.goalTimingRequired')}: ${capitalizeFirstLetter(
            goal?.goal_name?.label ?? '',
          )}`,
        );
      });
      if (invalidTimingGoals.length > 0) {
        return;
      }
    }

    setSubmitLoading(true);

    const res = await saveGoalsProgress();
    setSubmitLoading(false);
    if (!res || res.error) {
      return;
    }
    setStep(step + 1);
  };

  const openSessionConclusionStep = () => {
    if (signatureStep === SessionNoteSignatures.BtSignature) {
      if (validateDuration) {
        setOpenSignature(true);
      } else {
        setOpenConfirm(true);
      }
      return;
    }

    setOpenSignature(true);
  };

  const validateSessionNarrative = async () => {
    const sessionNarrative = getNarrativeText(values.narrative);
    if (!sessionNarrative) {
      toast.error('Session narrative must be recorded in a session');
      return;
    }
    if (sessionNarrative?.length < sessionNarrativeMinimumLength) {
      toast.error(
        `Session narrative requires at least ${sessionNarrativeMinimumLength} characters`,
      );
      return;
    }

    if (useNewUi && session?.evv_required && !isEndEvvCaptured) {
      if (!isStartEvvCaptured) {
        toast.error('Please complete start EVV before ending the session');
        return;
      }

      await startEvvVerification({
        kind: EVV_TOKEN_KINDS.END,
        replaceActive: true,
        onCaptured: openSessionConclusionStep,
      });
      return;
    }

    openSessionConclusionStep();
  };

  const handlePrevClick = async () => {
    if (!isEditable || updateDisabled) {
      setStep(step - 1);
      return;
    }

    let res;

    if (step === SessionNoteSteps.GoalsProgress) {
      if (session?.active_treatment_plan_id) {
        const invalidTrials = getInvalidTrialList(goalsPracticedList);
        invalidTrials?.forEach((trial) => {
          toast.error('Data for target ' + trial?.target?.label + ' has not been updated');
        });
        if (invalidTrials.length > 0) {
          return;
        }
      }
      setSubmitLoading(true);
      res = await saveGoalsProgress();
      setSubmitLoading(false);
    }

    if (step === SessionNoteSteps.SessionNarrative) {
      const existingNarrative = getNarrativeText(values.narrative);

      if (!existingNarrative) return;

      setSubmitLoading(true);
      const gptRes = await dispatch(
        sessionActions.getSessionNarrativeSuggestion({
          getAccessTokenSilently,
          sessionId: session?.id,
        }),
      );
      if (!gptRes.error && gptRes.payload?.narrative) {
        const latestNarrative = gptRes.payload?.narrative;
        if (existingNarrative && existingNarrative === getNarrativeText(latestNarrative)) {
          handleUpdateFieldValues('narrative', '');
        }
      }
      const data = {
        narrative: useNewUi ? values?.narrative : values?.narrative?.trim(),
      };
      res = await dispatch(
        sessionActions.updateSessionNarrative({
          sessionId: session?.id,
          data,
          getAccessTokenSilently,
        }),
      );
      setSubmitLoading(false);
    }

    if (!res || res.error) {
      return;
    }

    setStep(step - 1);
  };

  const handleSignature = async (dataURL: string, step: SessionNoteSignatures) => {
    if (step === SessionNoteSignatures.BtSignature) {
      handleBtSignature(dataURL);
    } else if (step === SessionNoteSignatures.ParentSignature) {
      handleParentSignature(dataURL);
    }
  };

  const handleSessionEndEarly = async () => {
    setOpenConfirm(false);
    const response = await dispatch(
      sessionActions.endSessionEarly({
        sessionId: session?.id,
        getAccessTokenSilently,
      }),
    );
    if (response.error) {
      return;
    }
    if (response?.payload?.narrative) {
      setValues({ ...values, narrative: response.payload.narrative });
    }
    const res = await dispatch(
      sessionActions.updateSessionNarrative({
        sessionId: session?.id,
        data: { narrative: response.payload.narrative },
        getAccessTokenSilently,
      }),
    );
    if (!res.error) {
      toast.success('Duration and session narrative have been updated');
    }
  };

  const handleBtSignature = async (dataURL: string) => {
    const btSignatureDate = moment(new Date());
    const data = {
      provider_signature: dataURL,
      date_provider_signed: btSignatureDate,
      utc_offset_date_provider_signed: getUtcOffset(btSignatureDate),
    };

    // Save BT signatures
    const res = await dispatch(
      sessionActions.saveBTSignartures({
        sessionId: session?.id,
        data,
        getAccessTokenSilently,
      }),
    );
    if (res.error) {
      setSubmitLoading(false);
      return;
    }
    // Edits requested session only needs BT Signatures to complete the flow
    if (
      (isVBT && !isBay7InPersonGuardianFlow) ||
      session?.status === BTSessionStatus.EditsRequested
    ) {
      history.navigate('/dashboard');
      return;
    }
    await dispatch(sessionActions.getSession({ sessionId: session?.id, getAccessTokenSilently }));
    setSignatureStep(SessionNoteSignatures.ParentSignature);
  };

  const handleParentSignature = async (dataURL: string) => {
    if (isBay7InPersonGuardianFlow) {
      if (!selectedParentEmail) {
        toast.error('Please select a parent email');
        return;
      }
      if (!isGuardianIdentityVerified || !guardianVerification) {
        toast.error('Please verify guardian identity before signing');
        return;
      }
    }

    const guardianSignatureDate = moment(new Date());
    const data: IGuardianSignaturePayload = {
      guardian_provider_signature: dataURL,
      date_guardian_provider_signed: guardianSignatureDate,
      utc_offset_date_guardian_provider_signed: getUtcOffset(guardianSignatureDate),
    };

    if (isBay7InPersonGuardianFlow) {
      let guardianAuthToken = '';
      try {
        guardianAuthToken = getGuardianAccessTokenSilently(
          guardianVerification,
          selectedParentEmail,
        );
      } catch (error: any) {
        toast.error(error?.message || 'Guardian verification expired. Please verify again.');
        clearGuardianVerificationCache();
        return;
      }

      data.guardian_auth_token = guardianAuthToken;
      data.session_id = session?.id;
      data.session_datetime = session?.date_start;
    }

    // Save guardian signature
    const res = isBay7InPersonGuardianFlow
      ? await dispatch(
          sessionActions.saveBay7InPersonGuardianSignature({
            sessionId: session?.id,
            data,
            getAccessTokenSilently,
          }),
        )
      : await dispatch(
          sessionActions.saveGuardianSignartures({
            sessionId: session?.id,
            data,
            getAccessTokenSilently,
          }),
        );

    if (res.error) {
      setSubmitLoading(false);
      return;
    }
    clearGuardianVerificationCache();
    setOpenSignature(false);
    history.navigate('/dashboard');
  };

  const renderSection = (values: any, updateFieldValue: any) => {
    switch (step) {
      case SessionNoteSteps.SymptomsAndInterventions:
        return (
          <SymptomsAndInterventions
            isEditable={isEditable && !updateDisabled}
            evidence_based_interventions_used={values.evidence_based_interventions_used}
            signs_and_symptoms_observed={values.signs_and_symptoms_observed}
            session={session}
            showErrors={showErrors.SymptomsInterventions}
            updateFieldValue={updateFieldValue}
            useNewUi={useNewUi}
          />
        );
      case SessionNoteSteps.GoalsProgress:
        return (
          <GoalsProgress
            isEditable={isEditable && !updateDisabled}
            values={values}
            showErrors={showErrors.GoalsProgress}
            updateFieldValue={updateFieldValue}
            setGoalsPracticedList={setGoalsPracticedList}
            useNewUi={useNewUi}
          />
        );
      case SessionNoteSteps.SessionNarrative:
        return (
          <SessionNarrative
            isEditable={isEditable && !updateDisabled}
            session={session}
            currentNarrative={values.narrative}
            minimumLength={sessionNarrativeMinimumLength}
            onChange={updateFieldValue}
            showError={showErrors.SessionNarrative}
            useNewUi={useNewUi}
          />
        );
      default:
        return <></>;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexGrow: 1, width: '100%' }}>
      <form
        style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', width: '100%' }}
        onSubmit={formik.handleSubmit}
      >
        {renderSection(values, handleUpdateFieldValues)}
        {session && (
          <SessionNotesFooter
            values={values}
            isEditable={isEditable}
            session={session}
            currentStep={step}
            loading={submitLoading}
            handlePrevClick={handlePrevClick}
            handleNextClick={handleNextClick}
          />
        )}
      </form>
      <FHConfirmDialog
        open={openConfirm}
        title="End session early"
        dialogStyles={{
          padding: '16px',
          maxHeight: '85vh',
          height: 'auto',
          width: '680px',
          maxWidth: '680px',
        }}
        contentRenderer={() => (
          <Typography fontSize={20} textAlign="left" p={2} lineHeight={1.7}>
            The actual session duration is shorter than the specified duration. If you proceed:
            <Box component="ul" sx={{ pl: 2, mt: 1, mb: 1 }}>
              <li>
                The <b>duration</b> will be adjusted accordingly.
              </li>
              <li>
                The <b>narrative</b> will be regenerated, and you must review it.
              </li>
            </Box>
          </Typography>
        )}
        submittext="Continue"
        submitBtnTestId="endEarlyContinueBtn"
        cancelBtnTestId="endEarlyCancelBtn"
        canceltext="Cancel"
        sx={{
          '& .MuiModal-backdrop': {
            backdropFilter: 'blur(2px)',
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
          },
        }}
        onClose={(_, reason) => {
          if (reason && reason === 'backdropClick') return;
          setOpenConfirm(false);
        }}
        onSubmit={handleSessionEndEarly}
      />
      {/* VBT Signature Dialog */}
      {isVBT && session && (
        <FHSignatureDialog
          headerText={signatureDialogHeaderText}
          cancelText="Clear Signature"
          open={openSignature}
          stepNumber={signatureStep === SessionNoteSignatures.BtSignature ? 1 : 2}
          isSignatureDisabled={isParentSignatureCanvasDisabled}
          onClose={() => setOpenSignature(false)}
          onSign={(dataUrl) => handleSignature(dataUrl, signatureStep)}
          contentRenderer={
            signatureStep === SessionNoteSignatures.ParentSignature && isBay7InPersonGuardianFlow
              ? () => (
                  <VerifyParentSignature
                    session={session}
                    providerAssignments={guardianProviderAssignments}
                    selectedParentEmail={selectedParentEmail}
                    onParentEmailChange={handleParentEmailChange}
                    onVerifyIdentity={handleGuardianIdentityVerification}
                    isGuardianVerificationLoading={guardianVerificationLoading}
                    isGuardianIdentityVerified={isGuardianIdentityVerified}
                    guardianVerificationExpiresAt={guardianVerification?.expiresAt ?? null}
                  />
                )
              : VBTSignatureMessage
          }
        />
      )}
      {!isVBT && (
        <FHSignatureDialog
          headerText={signatureDialogHeaderText}
          cancelText="Clear Signature"
          open={openSignature}
          stepNumber={signatureStep === SessionNoteSignatures.BtSignature ? 1 : 2}
          onClose={() => setOpenSignature(false)}
          onSign={(dataUrl) => handleSignature(dataUrl, signatureStep)}
          contentRenderer={
            signatureStep === SessionNoteSignatures.BtSignature
              ? BTSignatureMessage
              : ParentSignatureMessage
          }
        />
      )}
      {evvQrDialog && (
        <EvvQrDialog
          open
          kind={evvQrDialog.kind}
          route={evvQrDialog.route}
          sessionId={evvQrDialog.sessionId}
          onClose={closeEvvQrDialog}
        />
      )}
    </Box>
  );
}

export default SegmentSlider;
